// /downloads: direct build downloads for every platform. Both the downloads
// themselves (→ functions/download*.ts → R2) and the version metadata
// (/api/v1/update* → manifest objects in R2) are served same-origin, so the
// page never depends on GitHub and keeps working through any repo move.

import type { ReactNode } from 'react';
import { Download, ExternalLink, MessageCircle } from 'lucide-react';
import { SiAndroid, SiAppstore, SiFdroid, SiLinux } from '@icons-pack/react-simple-icons';
import { AppleIcon, WindowsIcon } from '../components/PlatformIcons';
import { SettingsSection, SettingsRow } from '../components/settings/_primitives';
import {
  DOWNLOAD_URL,
  WINDOWS_INSTALLER_NAME,
  MAC_DOWNLOAD_URL,
  LINUX_DOWNLOAD_URL,
  ANDROID_DOWNLOAD_URL,
  FDROID_URL,
  TESTFLIGHT_URL,
  TESTFLIGHT_APP_URL,
  UPDATE_MANIFEST_URL,
  ANDROID_MANIFEST_URL,
  RELEASES_URL,
  DISCORD_URL,
  LINUX_LAST_VERSION,
  LINUX_LAST_RELEASE_URL,
} from '../lib/downloads';
import {
  useVisitorPlatform,
  useReleaseManifest,
  macBuildOf,
  linuxBuildOf,
  useDownloadSize,
  formatBytes,
  type ManifestState,
  type ManifestStatus,
} from '../lib/releaseManifest';

export default function Downloads() {
  const desktop = useReleaseManifest(UPDATE_MANIFEST_URL);
  const android = useReleaseManifest(ANDROID_MANIFEST_URL);
  const visitor = useVisitorPlatform();

  return (
    <div className="space-y-8">
      <div className="px-1">
        <h1 className="text-3xl font-semibold tracking-tight">Downloads</h1>
        {/* One line, and only the part a download page is asked for. What the
            app IS belongs on the landing page these visitors came through. */}
        <p className="mt-2 max-w-xl text-textSecondary">Free and open source.</p>
      </div>

      <SettingsSection label="Platforms" bare>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {/* An iPhone can install nothing else on this page, so its card leads
              there instead of sitting fifth below four builds it cannot run. */}
          {visitor === 'iphone' && <IPhoneCard />}
          <DesktopCard state={desktop} />
          <MacCard desktop={desktop} />
          <LinuxCard desktop={desktop} />
          <AndroidCard state={android} />
          {visitor !== 'iphone' && <IPhoneCard />}
        </div>
      </SettingsSection>

      <SettingsSection
        id="macos"
        label="Installing on macOS"
        description="StreamNook is not in the App Store and not yet notarized with Apple, so the very first launch takes one extra step. Updates after that install themselves with no prompts."
      >
        <SettingsRow
          title="Unpack and move it"
          description="Double-click the archive if your browser did not unpack it, then drag StreamNook to your Applications folder."
        />
        <SettingsRow
          title="Open it once from System Settings"
          description="The first time you open it, macOS says it could not verify the app and offers only Done. Open System Settings, go to Privacy & Security, scroll to the Security section, and click Open Anyway next to StreamNook. Confirm with Open. macOS remembers the choice."
        />
        <SettingsRow
          title="Getting later builds"
          description="StreamNook checks for updates itself and installs them in place. Updates it downloads are not quarantined, so the extra step never comes back."
        />
      </SettingsSection>

      <SettingsSection
        id="android"
        label="Installing on Android"
        description="The Android build is not on the Play Store. Install the APK from this page, or add the StreamNook repository to F-Droid. Either way, the first install takes two extra taps."
      >
        <SettingsRow
          title="Allow the install"
          description="Android asks once, when you open the downloaded file. Approve the browser you downloaded it with (or F-Droid, if you install through it), or find it later under Settings, Apps, Special access, Install unknown apps."
        />
        <SettingsRow
          title="Expect a scan prompt"
          description="Play Protect checks apps that did not come from the Play Store and shows a notice before it installs. Choosing to continue is the expected path here."
        />
        <SettingsRow
          title="Getting later builds"
          description="StreamNook tells you when a new build is out. Download it over the top of the old one; your login and settings are kept. With F-Droid, updates arrive through F-Droid instead."
        />
        <SettingsRow
          title="Adding the F-Droid repository"
          description="StreamNook is not in the main F-Droid catalog; it comes from its own repository. With an F-Droid app installed (F-Droid, Droid-ify or Neo Store), open Add to F-Droid on your phone and tap Add the StreamNook repository, then install StreamNook from F-Droid. The page also lists the address and fingerprint for adding it by hand."
        />
      </SettingsSection>

      <SettingsSection
        id="iphone"
        label="Installing on iPhone"
        description="The iPhone build is a beta on TestFlight, Apple's app for testing builds before they reach the App Store."
      >
        <SettingsRow
          title="Get TestFlight"
          description="Install TestFlight from the App Store on the iPhone you want StreamNook on."
        />
        <SettingsRow
          title="Open the invite"
          description="Open the TestFlight link on that iPhone, tap Accept, then Install."
        />
        <SettingsRow
          title="Getting later builds"
          description="TestFlight offers each new build as an update. A TestFlight build stops opening 90 days after it was uploaded, so install the updates it offers."
        />
      </SettingsSection>

      <SettingsSection label="More" bare>
        <a
          href={RELEASES_URL}
          target="_blank"
          rel="noreferrer"
          className="settings-card flex items-center justify-between p-5 transition-colors hover:bg-white/[0.03]"
        >
          <div>
            <p className="text-[13px] font-medium text-textPrimary">All releases on GitHub</p>
            <p className="mt-0.5 text-[12px] text-textSecondary">
              Full history, prereleases, archived versions.
            </p>
          </div>
          <ExternalLink className="h-4 w-4 text-textMuted" />
        </a>
      </SettingsSection>
    </div>
  );
}

/* ─── Platform cards ───────────────────────────────────────────────────── */

/** A card carries no description. Somebody on this page has already chosen
 *  StreamNook, so a paragraph selling it back to them is noise between them and
 *  the file. Every per-platform fact worth having is either a spec or already on
 *  the button, and each card's blurb had drifted into repeating both. */
function PlatformCard({
  icon,
  name,
  badge,
  children,
  specs,
}: {
  icon: ReactNode;
  name: string;
  badge?: string;
  children: ReactNode;
  specs: string[];
}) {
  return (
    <div className="settings-card flex flex-col gap-5 p-7">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.04] text-accent">
          {icon}
        </div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold tracking-tight">{name}</h2>
          {badge && (
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-accent">
              {badge}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">{children}</div>

      {/* The specs carry `mt-auto`, not the download block. Every header is one
          line, so putting the button straight under it aligns the buttons across
          the row, and pinning the specs to the bottom aligns their rules too.
          Holding the download block down instead left a void under the header
          once the description came out. */}
      <ul className="mt-auto space-y-1.5 border-t border-[var(--color-borderSubtle)] pt-4">
        {specs.map(spec => (
          <li key={spec} className="text-[12px] leading-relaxed text-textMuted">
            {spec}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The primary download control every card shares. */
function DownloadButton({
  href,
  filename,
  label,
  size,
}: {
  href: string;
  filename: string;
  label: string;
  size?: number;
}) {
  return (
    <a
      href={href}
      download={filename}
      className="btn-primary inline-flex items-center justify-center gap-2.5 px-5 py-3 text-sm font-semibold"
    >
      <Download className="h-4 w-4" />
      {label}
      {size !== undefined && (
        <span className="text-[11px] font-normal tabular-nums opacity-70">{formatBytes(size)}</span>
      )}
    </a>
  );
}

/** What a card shows in place of a download before its first build exists. */
function NotYetPublished({ note }: { note: string }) {
  return (
    <>
      <a
        href={DISCORD_URL}
        target="_blank"
        rel="noreferrer"
        className="glass-button inline-flex items-center justify-center gap-2.5 px-5 py-3 text-sm font-medium"
      >
        <MessageCircle className="h-4 w-4" />
        Join the Discord
      </a>
      <p className="text-[12px] text-textMuted">{note}</p>
    </>
  );
}

function DesktopCard({ state }: { state: ManifestState }) {
  const { manifest } = state;
  const version = manifest?.version ? `v${manifest.version}` : null;
  // The manifest's flat bundle_name and size describe the legacy portable 7z
  // that old clients update from, not the installer this button serves, so
  // the filename is fixed and the size is read off the download itself.
  const size = useDownloadSize(DOWNLOAD_URL);

  return (
    <PlatformCard
      icon={<WindowsIcon className="h-5 w-5" />}
      name="Windows"
      specs={[
        'Windows 10 (1809 or newer) and Windows 11',
        'x86_64. ARM64 builds are not shipped yet.',
        'Run the installer. It installs for your account only, needs no admin rights, and updates itself from then on.',
      ]}
    >
      {/* The download streams from R2 and never depends on the manifest fetch,
          so the button is always live. The manifest only enriches it. */}
      <DownloadButton
        href={DOWNLOAD_URL}
        filename={WINDOWS_INSTALLER_NAME}
        label={version ? `Download ${version}` : 'Download for Windows'}
        size={size}
      />
      <MetaLine
        status={state.status}
        bundle={manifest ? WINDOWS_INSTALLER_NAME : undefined}
        publishedAt={manifest?.published_at}
        fallback="Windows 10 and 11 · 64-bit"
      />
    </PlatformCard>
  );
}

function MacCard({ desktop }: { desktop: ManifestState }) {
  // The Mac bundle is published by the same release as the Windows one and
  // described in the same manifest, so its version and date are the desktop
  // manifest's. Only its size and filename are its own.
  const { build, status } = macBuildOf(desktop);
  const version = desktop.manifest?.version ? `v${desktop.manifest.version}` : null;

  return (
    <PlatformCard
      icon={<AppleIcon className="h-6 w-6" />}
      name="macOS"
      badge="New"
      specs={[
        'macOS 11 Big Sur or newer',
        'Universal build: Apple silicon and Intel in one app.',
        'Not yet notarized with Apple, so the first launch takes one extra step. See below.',
      ]}
    >
      {status === 'unpublished' ? (
        <NotYetPublished note="The first Mac build is not out yet. The Discord is where it gets announced." />
      ) : (
        <>
          <DownloadButton
            href={MAC_DOWNLOAD_URL}
            filename={build?.bundle_name ?? 'StreamNook.app.tar.gz'}
            label={version ? `Download ${version}` : 'Download for macOS'}
            size={build?.size}
          />
          <MetaLine
            status={status}
            bundle={build?.bundle_name}
            publishedAt={desktop.manifest?.published_at}
            fallback="macOS 11 and newer · Universal"
          />
        </>
      )}
    </PlatformCard>
  );
}

function LinuxCard({ desktop }: { desktop: ManifestState }) {
  // Same manifest as Windows and macOS: one desktop release describes every
  // desktop OS, so the version and date are the manifest's and only the size
  // and filename are this platform's.
  //
  // A manifest with no Linux entry means Linux releases are paused, not that
  // none ever shipped: the last AppImage is still served, so the card offers it
  // and says it will not update. A manifest that failed to load says nothing
  // either way, so the plain download stays.
  const { build, status } = linuxBuildOf(desktop);
  const version = desktop.manifest?.version ? `v${desktop.manifest.version}` : null;
  const paused = desktop.status === 'ready' && !build;

  return (
    <PlatformCard
      icon={<SiLinux className="h-6 w-6" />}
      name="Linux"
      badge="New"
      specs={[
        'x86_64. No arm64 build yet.',
        'Any desktop with GTK and WebKitGTK, which is every mainstream distribution.',
        paused ? 'Mark it executable and run it.' : 'Mark it executable and run it. It updates itself from then on.',
      ]}
    >
      {paused ? (
        <>
          <DownloadButton
            href={LINUX_DOWNLOAD_URL}
            filename="StreamNook.AppImage"
            label={`Download v${LINUX_LAST_VERSION}`}
          />
          <p className="text-[12px] text-textMuted">
            Linux builds are paused for now.{' '}
            <a
              href={LINUX_LAST_RELEASE_URL}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-white/20 underline-offset-2 transition-colors hover:text-textPrimary"
            >
              {LINUX_LAST_VERSION}
            </a>{' '}
            is the last one, and it does not update itself.
          </p>
        </>
      ) : (
        <>
          <DownloadButton
            href={LINUX_DOWNLOAD_URL}
            filename={build?.bundle_name ?? 'StreamNook.AppImage'}
            label={version ? `Download ${version}` : 'Download for Linux'}
            size={build?.size}
          />
          <MetaLine
            status={status}
            bundle={build?.bundle_name}
            publishedAt={desktop.manifest?.published_at}
            fallback="Linux · x86_64 · AppImage"
          />
        </>
      )}
    </PlatformCard>
  );
}

function AndroidCard({ state }: { state: ManifestState }) {
  const { manifest, status } = state;
  const version = manifest?.version ? `v${manifest.version}` : null;

  return (
    <PlatformCard
      icon={<SiAndroid className="h-6 w-6" />}
      name="Android"
      specs={[
        'Android 8.0 or newer',
        '64-bit ARM phones. Tablet layouts are not tuned yet.',
        'Install the APK from this page, or through F-Droid, which keeps it updated.',
      ]}
    >
      {status === 'unpublished' ? (
        <NotYetPublished note="The first Android build is not out yet. The Discord is where it gets announced." />
      ) : (
        <>
          <DownloadButton
            href={ANDROID_DOWNLOAD_URL}
            filename={manifest?.bundle_name ?? 'StreamNook.apk'}
            label={version ? `Download ${version}` : 'Download for Android'}
            size={manifest?.size}
          />
          <a
            href={FDROID_URL}
            className="glass-button inline-flex items-center justify-center gap-2.5 px-5 py-3 text-sm font-medium"
          >
            <SiFdroid className="h-4 w-4" />
            Add to F-Droid
          </a>
          <MetaLine
            status={status}
            bundle={manifest?.bundle_name}
            publishedAt={manifest?.published_at}
            fallback="Android 8.0+ · 64-bit ARM"
          />
        </>
      )}
    </PlatformCard>
  );
}

function IPhoneCard() {
  // TestFlight hosts the build and its updates, so there is no manifest to
  // read and nothing to download from this site: the card links out.
  return (
    <PlatformCard
      icon={<AppleIcon className="h-6 w-6" />}
      name="iPhone"
      badge="Beta"
      specs={[
        'iOS 26 or newer',
        'iPhone only. No iPad build yet.',
        'Installs through TestFlight, which keeps it updated.',
      ]}
    >
      <a
        href={TESTFLIGHT_URL}
        target="_blank"
        rel="noreferrer"
        className="btn-primary inline-flex items-center justify-center gap-2.5 px-5 py-3 text-sm font-semibold"
      >
        <ExternalLink className="h-4 w-4" />
        Join on TestFlight
      </a>
      <a
        href={TESTFLIGHT_APP_URL}
        target="_blank"
        rel="noreferrer"
        className="glass-button inline-flex items-center justify-center gap-2.5 px-5 py-3 text-sm font-medium"
      >
        <SiAppstore className="h-4 w-4" />
        Get TestFlight
      </a>
      <p className="text-[12px] text-textMuted">iOS 26+ · iPhone · TestFlight</p>
    </PlatformCard>
  );
}

function MetaLine({
  status,
  bundle,
  publishedAt,
  fallback,
}: {
  status: ManifestStatus;
  bundle?: string;
  publishedAt?: string;
  fallback: string;
}) {
  if (status === 'loading') {
    return <p className="text-[12px] text-textMuted">Checking for the latest version…</p>;
  }
  if (bundle && publishedAt) {
    return (
      <p className="text-[12px] text-textMuted">
        {bundle} · Released {new Date(publishedAt).toLocaleDateString()}
      </p>
    );
  }
  return <p className="text-[12px] text-textMuted">{fallback}</p>;
}
